import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import * as Icons from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Gauge, TrendingUp, TrendingDown, Activity, ShieldCheck, MonitorCog, Download, FileText, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import IntruderSummaryCard from "../components/IntruderSummaryCard";
import { fmtMDT } from "../lib/time";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const STATUS_COLORS = {
  operational: "text-emerald-600 dark:text-emerald-400",
  degraded: "text-amber-600 dark:text-amber-400",
  down: "text-rose-600 dark:text-rose-400",
};

function Sparkline({ data, color = "#49C22E", height = 40 }) {
  const pts = (data || []).map((v, i) => ({ i, v }));
  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <AreaChart data={pts} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <defs>
            <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.35} />
              <stop offset="95%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill="url(#sparkGrad)" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, tone = "primary" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
        <Icon className="w-4 h-4 text-primary" /> {label}
      </div>
      <div className="mt-1 text-[26px] font-semibold tracking-tight">{value}</div>
      {hint && <div className="text-[11.5px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function downloadCsv(name, rows) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}

export default function UCVigil() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/uc-vigil/summary");
      setData(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const trendPoints = useMemo(() => {
    if (!data) return [];
    const days = 30;
    return Array.from({ length: days }, (_, i) => ({
      day: `D-${days - i}`,
      compliance: data.trends.compliance[i],
      focus: data.trends.focus[i],
      blocked: data.trends.blocked[i],
      endpoints: data.trends.endpoints_online[i],
    }));
  }, [data]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["app", "category", "status", "uptime_30d", "metric_label", "metric_value", "metric_trend", "last_incident"]];
    data.apps.forEach((a) => rows.push([a.name, a.category, a.status, a.uptime_30d, a.metric_label, a.metric_value, a.metric_trend, a.last_incident]));
    downloadCsv(`uc-vigil-${new Date().toISOString().slice(0,10)}.csv`, rows);
  };

  const exportPdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFontSize(18); doc.text("UC Vigil — Aggregate Report", 40, 50);
    doc.setFontSize(10); doc.setTextColor(120); doc.text(`Generated ${fmtMDT(data.generated_at)}`, 40, 68);
    doc.setTextColor(20);
    const k = data.kpis;
    const kpiLines = [
      ["Total apps", k.total_apps],
      ["Operational", k.operational],
      ["Degraded", k.degraded],
      ["Down", k.down],
      ["Avg uptime (30d)", `${k.avg_uptime_30d}%`],
      ["Controls passing", k.controls_passing],
      ["Focus score", k.focus_score],
      ["Blocked events (24h)", k.blocked_events_24h],
    ];
    autoTable(doc, { startY: 90, head: [["KPI", "Value"]], body: kpiLines, styles: { fontSize: 10 }, headStyles: { fillColor: [32, 107, 196] } });
    autoTable(doc, {
      head: [["App", "Category", "Status", "Uptime 30d", "Metric", "Last incident"]],
      body: data.apps.map((a) => [a.name, a.category, a.status, `${a.uptime_30d}%`, `${a.metric_label || ""}: ${a.metric_value || ""}`, fmtMDT(a.last_incident, { withTz: false })]),
      styles: { fontSize: 9 }, headStyles: { fillColor: [32, 107, 196] },
    });
    doc.save(`uc-vigil-${new Date().toISOString().slice(0,10)}.pdf`);
  };

  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-muted/40 animate-pulse" />)}
      </div>
    );
  }

  const k = data.kpis;
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-primary text-[12px] font-semibold uppercase tracking-wider"><Gauge className="w-3.5 h-3.5" /> UC Vigil</div>
          <h1 className="text-[26px] font-semibold tracking-tight mt-1">Aggregate compliance & health</h1>
          <p className="text-[13px] text-muted-foreground">Executive view across NinjaOne, ThreatLocker, Insights and Compliance Vault.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5"><RefreshCw className="w-4 h-4" /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={exportCsv} className="gap-1.5"><Download className="w-4 h-4" /> CSV</Button>
          <Button size="sm" onClick={exportPdf} className="gap-1.5"><FileText className="w-4 h-4" /> PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Activity} label="Apps monitored" value={k.total_apps} hint={`${k.operational} operational · ${k.degraded} degraded · ${k.down} down`} />
        <Kpi icon={MonitorCog} label="Avg uptime (30d)" value={`${k.avg_uptime_30d}%`} />
        <Kpi icon={ShieldCheck} label="Controls passing" value={k.controls_passing} hint="Compliance Vault" />
        <Kpi icon={TrendingUp} label="Focus score" value={k.focus_score} hint="Company-wide" />
      </div>

      <IntruderSummaryCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-[14px] font-semibold">Compliance controls passing</h3>
            <span className="text-[12px] text-muted-foreground">30 days</span>
          </div>
          <div className="mt-2 h-56">
            <ResponsiveContainer><LineChart data={trendPoints}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis domain={["dataMin - 2", "dataMax + 2"]} tick={{ fontSize: 10 }} /><Tooltip /><Line type="monotone" dataKey="compliance" stroke="hsl(212 71% 45%)" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-[14px] font-semibold">Blocked events (ThreatLocker)</h3>
            <span className="text-[12px] text-muted-foreground">30 days</span>
          </div>
          <div className="mt-2 h-56">
            <ResponsiveContainer><AreaChart data={trendPoints}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Area type="monotone" dataKey="blocked" stroke="hsl(0 72% 51%)" fill="hsl(0 72% 51% / 0.15)" strokeWidth={2} /></AreaChart></ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-[14px] font-semibold">Per-app health</h3>
        </div>
        <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
          <div className="col-span-3">App</div><div className="col-span-2">Category</div><div className="col-span-2">Status</div><div className="col-span-2">Uptime 30d</div><div className="col-span-3">30-day sparkline</div>
        </div>
        <div className="divide-y divide-border">
          {data.apps.map((a) => {
            const Icon = Icons[a.icon] || Icons.LayoutGrid;
            return (
              <div key={a.id} className="grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-muted/30">
                <div className="col-span-3 flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"><Icon className="w-4 h-4 text-primary" /></div><span className="font-semibold text-[13.5px]">{a.name}</span></div>
                <div className="col-span-2 text-[12.5px] text-muted-foreground capitalize">{String(a.category).replace("_", " ")}</div>
                <div className={cn("col-span-2 text-[12.5px] font-semibold", STATUS_COLORS[a.status])}>{a.status}</div>
                <div className="col-span-2 text-[12.5px] font-semibold">{a.uptime_30d}%</div>
                <div className="col-span-3"><Sparkline data={a.uptime_series} /></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
